import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { parseMarkdown } from '../utils/markdown.js';

const renderInline = (text) => {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <Box
          key={`${part}-${index}`}
          component="code"
          sx={{
            px: 0.8,
            py: 0.2,
            borderRadius: '6px',
            backgroundColor: '#e2e8f0',
            color: '#0f172a',
            fontFamily: 'Menlo, Monaco, Consolas, monospace',
            fontSize: '0.92em',
          }}
        >
          {part.slice(1, -1)}
        </Box>
      );
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box key={`${part}-${index}`} component="strong" sx={{ fontWeight: 700, color: '#0f172a' }}>
          {part.slice(2, -2)}
        </Box>
      );
    }

    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

function MarkdownRenderer({ source }) {
  const blocks = parseMarkdown(source);

  return (
    <Box>
      {blocks.map((block, index) => {
        if (block.type === 'heading') {
          const headingVariant = block.level === 1 ? 'h4' : block.level === 2 ? 'h5' : 'h6';

          return (
            <Typography
              key={`heading-${index}`}
              variant={headingVariant}
              sx={{
                mt: index === 0 ? 0 : 4,
                mb: 1.4,
                fontWeight: 800,
                color: '#0f172a',
                lineHeight: 1.3,
              }}
            >
              {renderInline(block.text)}
            </Typography>
          );
        }

        if (block.type === 'paragraph') {
          return (
            <Typography
              key={`paragraph-${index}`}
              sx={{
                mb: 2,
                color: '#334155',
                lineHeight: 1.95,
                fontSize: '1.02rem',
              }}
            >
              {renderInline(block.text)}
            </Typography>
          );
        }

        if (block.type === 'list') {
          return (
            <Box key={`list-${index}`} component="ul" sx={{ pl: 3, my: 2.5, color: '#334155' }}>
              {block.items.map((item) => (
                <Box key={item} component="li" sx={{ mb: 1.1, lineHeight: 1.85 }}>
                  {renderInline(item)}
                </Box>
              ))}
            </Box>
          );
        }

        if (block.type === 'code') {
          return (
            <Box
              key={`code-${index}`}
              sx={{
                mb: 2.5,
                borderRadius: '14px',
                border: '1px solid #cbd5e1',
                backgroundColor: '#0f172a',
                overflowX: 'auto',
              }}
            >
              <Box sx={{ px: 2, py: 0.9, borderBottom: '1px solid rgba(148, 163, 184, 0.3)', color: '#93c5fd', fontSize: '0.82rem' }}>
                {block.language || 'text'}
              </Box>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  px: 2,
                  py: 2,
                  color: '#e2e8f0',
                  fontFamily: 'Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.92rem',
                  lineHeight: 1.65,
                }}
              >
                {block.text}
              </Box>
            </Box>
          );
        }

        return null;
      })}
    </Box>
  );
}

export default MarkdownRenderer;

